<template>
  <div>
    <div class="row page-title-header">
      <div class="col-12">
        <div class="page-header">
          <b-breadcrumb class="m-0">
            <b-breadcrumb-item
              :to="{
                name: 'DashboardHome',
              }"
            >
              <i class="fa fa-home"></i>
            </b-breadcrumb-item>
            <b-breadcrumb-item active>控制中心</b-breadcrumb-item>
            <b-breadcrumb-item :to="{ name: 'Branches' }">分店管理</b-breadcrumb-item>
            <b-breadcrumb-item active>分店選單</b-breadcrumb-item>
          </b-breadcrumb>
        </div>
      </div>
    </div>

    <b-overlay :show="showLoading">
      <b-card>
        <div class="row mb-2">
          <div class="col-12 d-flex items-center justify-content-between">
            <h4 class="font-weight-bold">分店選單編輯</h4>
          </div>
        </div>
        <div class="menu-editor-container">
          <VueDraggable
            v-model="menuItems"
            :options="{ handle: '.sort-handle', animation: 200 }"
            @end="onMenuItemsReorder"
          >
            <div
              v-for="(menuItem, index) in menuItems"
              :key="index"
              class="menu-item-row d-flex align-items-center mb-3 p-3 border rounded"
            >
              <!-- Sort Icon -->
              <div class="sort-handle mr-3" style="cursor: move;">
                <i class="mdi mdi-sort text-muted" style="font-size: 1.2em;"></i>
                <span class="ml-2 text-secondary">{{ index + 1 }}</span>
              </div>

              <!-- Checkbox -->
              <div class="mr-3">
                <b-form-checkbox
                  v-model="menuItem.is_enabled"
                  switch
                ></b-form-checkbox>
              </div>

              <!-- Title Input -->
              <div class="mr-3" style="width: 15em;">
                <b-form-input
                  v-model="menuItem.action.label"
                  placeholder="請輸入標題"
                  :class="{ 'is-invalid': menuItem.is_enabled && !menuItem.action.label }"
                  :required="menuItem.is_enabled"
                  :disabled="shouldOverwriteLabel(menuItem.action.type)"
                />
                <div v-if="menuItem.is_enabled && !menuItem.action.label" class="invalid-feedback">
                  此欄位為必填
                </div>
              </div>

              <!-- Type Dropdown -->
              <div class="mr-3" style="min-width: 150px;">
                <b-form-select
                  v-model="menuItem.action.type"
                  :options="actionTypeOptions"
                  :class="{ 'is-invalid': menuItem.is_enabled && !menuItem.action.type }"
                  :required="menuItem.is_enabled"
                  @change="e => onActionTypeChange(e, index)"
                />
                <div v-if="menuItem.is_enabled && !menuItem.action.type" class="invalid-feedback">
                  此欄位為必填
                </div>
              </div>

              <!-- Action Input -->
              <div class="flex-grow-1">
                <b-form-input
                  v-if="menuItem.action.type === 'uri'"
                  v-model="menuItem.action.uri"
                  placeholder="請輸入網址"
                  :class="{
                    'is-invalid': 
                      (menuItem.action.uri && !isValidUrl(menuItem.action.uri)) || 
                      (menuItem.is_enabled && !menuItem.action.uri)
                  }"
                  :required="menuItem.is_enabled"
                />
                <b-form-textarea
                  v-else-if="menuItem.action.type === 'message'"
                  v-model="menuItem.action.text"
                  placeholder="於此輸入的文字會傳送至聊天室"
                  rows="2"
                  :class="{ 'is-invalid': menuItem.is_enabled && !menuItem.action.text }"
                  :required="menuItem.is_enabled"
                />
                <div v-if="menuItem.action.type === 'uri'">
                  <div v-if="menuItem.is_enabled && !menuItem.action.uri" class="invalid-feedback">
                    此欄位為必填
                  </div>
                  <div v-else-if="menuItem.action.uri && !isValidUrl(menuItem.action.uri)" class="invalid-feedback">
                    請輸入有效的網址（必須以 http:// 或 https:// 開頭）
                  </div>
                </div>
                <div v-if="menuItem.is_enabled && menuItem.action.type === 'message'">
                  <div v-if="!menuItem.action.text" class="invalid-feedback">
                    此欄位為必填
                  </div>
                </div>
              </div>
            </div>
          </VueDraggable>
        </div>

        <!-- 表單底部按鈕區塊 -->
        <div class="d-flex justify-content-center mt-4">
          <b-button
            class="mr-3"
            variant="outline-danger"
            @click="handleCancel"
            :disabled="showLoading"
          >
            返回
          </b-button>
          <b-button
            variant="success"
            @click="handleSave"
            :disabled="showLoading"
          >
            儲存
          </b-button>
        </div>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import branchApi from "@/apis/branch";
import _ from "lodash"
import VueDraggable from "vuedraggable";
import {mapGetters} from "vuex";

export default {
  name: "BranchMenuEditor",
  components: { VueDraggable },
  data() {
    return {
      showLoading: false,
      menuItems: [],
      defaultTypes: ['booking', 'branch_info', 'google_map'],
      actionTypeOptions: [
        { value: "", text: "請選擇類型", disabled: true },
        { value: "uri", text: "網址" },
        // { value: "message", text: "文字訊息" },
        { value: "booking", text: "立即預約" },
        { value: "branch_info", text: "查看店家" },
        { value: "google_map", text: "開啟地圖" }
      ]
    };
  },
  computed: {
    ...mapGetters("route", ["from", "routeQuery", "routeParams"]),
    branchID() {
      return this.$route.params.branchID;
    }
  },
  mounted() {
    this.fetchMenuConfig();
  },
  methods: {
    getDefaultMenuItem() {
      return {
        type: "button",
        style: "secondary",
        is_enabled: false,
        order: 1,
        action: {
          type: "",
          label: "",
          uri: "",
          text: ""
        }
      };
    },
    /**
     * 取得分店選單設定，常駐8個項目
     */
    async fetchMenuConfig() {
      try {
        this.showLoading = true;
        const { data } = await branchApi.getBranchMenuConfig(this.branchID);

        let items = Array.isArray(data.data.menus) ? data.data.menus : [];

        // 如果 menus 是空的，使用預設順序
        if (items.length === 0) {
          items = [
            {
              type: "button",
              style: "secondary",
              is_enabled: false,
              order: 1,
              action: { type: "booking", label: "" }
            },
            {
              type: "button",
              style: "secondary",
              is_enabled: false,
              order: 2,
              action: { type: "branch_info", label: "" }
            },
            {
              type: "button",
              style: "secondary",
              is_enabled: false,
              order: 3,
              action: { type: "google_map", label: "" }
            }
          ];
        } else {
          const existingTypes = items.map(item => item.action?.type);

          this.defaultTypes.forEach(type => {
            if (!existingTypes.includes(type)) {
              items.push({
                type: "button",
                style: "secondary",
                is_enabled: false,
                order: items.length + 1,
                action: { type, label: "" }
              });
            }
          });
        }

        let processedItems = items.map(menu => {
          const currentTypeIsValid = this.actionTypeOptions.some(option => option.value === menu.action?.type)
          const desiredLabel = menu.action?.label || (this.shouldOverwriteLabel(menu.action?.type) ? this.getMenuItemLabel(menu.action?.type) : null) || ""

          return {
            type: menu.type || "button",
            style: menu.style || "secondary",
            is_enabled: menu.is_enabled !== undefined ? menu.is_enabled : false,
            order: menu.order || 0,
            action: {
              type: currentTypeIsValid ? (menu.action?.type || "") : "",
              label: currentTypeIsValid ? desiredLabel : this.getMenuItemLabel(""),
              uri: menu.action?.uri || "",
              text: menu.action?.text || ""
            }
          }
        })

        processedItems = processedItems.sort((a, b) => (a.order || 0) - (b.order || 0));

        while (processedItems.length < 6) {
          processedItems.push(this.getDefaultMenuItem());
        }

        processedItems.forEach((item, idx) => {
          item.order = idx + 1;
        });

        this.menuItems = processedItems;
        console.log("Menu config loaded:", { menuItems: this.menuItems });
      } catch (error) {
        console.error("Failed to load menu config:", error);
        this.$swal.fire({
          type: "error",
          title: "載入失敗",
          text: "無法載入選單設定，請稍後再試"
        });
      } finally {
        this.showLoading = false;
      }
    },
    onActionTypeChange(value, index) {
      // Clear previous action data when type changes
      this.menuItems[index].action.uri = "";
      this.menuItems[index].action.text = "";
      if (this.shouldOverwriteLabel(value)) {
        this.menuItems[index].action.label = this.getMenuItemLabel(value)
      }
    },
    getMenuItemLabel(actionType) {
      return _.get(this.actionTypeOptions.find(option => option.value === actionType), 'text')
    },
    shouldOverwriteLabel(actionType) {
      return ['booking', 'branch_info', 'google_map'].includes(actionType)
    },
    isValidUrl(url) {
      try {
        const parsed = new URL(url);
        const pattern = /^(https?:\/\/)((localhost|(\d{1,3}\.){3}\d{1,3})|([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,})(:\d+)?(\/[^\s]*)?$/;
        return pattern.test(parsed.href);
      } catch (e) {
        return false;
      }
    },
    validateForm() {
      let isValid = true;

      this.menuItems.forEach((item) => {
        if (item.action.type === "uri") {
          if (item.action.uri && !this.isValidUrl(item.action.uri)) {
            isValid = false;
          }
          // URL is required only if enabled
          if (item.is_enabled && !item.action.uri) {
            isValid = false;
          }
        }
        else if (item.is_enabled) {
          if (!item.action.type) {
            isValid = false;
          }
          if (item.action.type === "message" && !item.action.text) {
            isValid = false;
          }
        }
      });

      return isValid;
    },
    async handleSave() {
      if (!this.validateForm()) {
        this.$swal.fire({
          type: "error",
          title: "表單驗證失敗",
          text: "請檢查必填欄位是否都已填寫"
        });
        return;
      }
      try {
        this.showLoading = true;
        // 過濾掉完全等於預設值的 menuItem
        const defaultItem = this.getDefaultMenuItem();
        const menus = this.menuItems
          .map((item, idx) => ({
            ...item,
            order: idx + 1
          }))
          .filter(item => {
            if (this.defaultTypes.includes(item.action.type)) {
              return item.action.label || item.is_enabled;
            }

            return !(
              item.type === defaultItem.type &&
              item.style === defaultItem.style &&
              item.is_enabled === defaultItem.is_enabled &&
              item.action.type === defaultItem.action.type &&
              item.action.uri === defaultItem.action.uri &&
              item.action.text === defaultItem.action.text &&
              item.action.label === defaultItem.action.label
            );
          })
          .map((item, idx) => {
            const action = { type: item.action.type, label: item.action.label };
            if (item.action.type === "uri") action.uri = item.action.uri;
            if (item.action.type === "message") action.text = item.action.text;
            return {
              type: item.type,
              style: item.style,
              is_enabled: item.is_enabled,
              order: idx + 1,
              action
            };
          });
        await branchApi.updateBranchMenuConfig(this.branchID, { menus });
        this.$swal.fire({
          type: "success",
          title: "儲存成功",
          text: "分店選單設定已成功更新"
        }).then(() => {
          this.handleCancel();
        });
      } catch (error) {
        console.error("Failed to save menu config:", error);
        this.$swal.fire({
          type: "error",
          title: "儲存失敗",
          text: "儲存選單設定時發生錯誤，請稍後再試"
        });
      } finally {
        this.showLoading = false;
      }
    },
    handleCancel() {
      if (this.from && this.from.name) {
        this.$router.push({ name: this.from.name, query: this.routeQuery, params: this.routeParams });
      } else {
        this.$router.push({ name: "Branches", query: this.routeQuery});
      }
    },
    // 拖曳排序後自動更新 order
    onMenuItemsReorder() {
      this.menuItems.forEach((item, idx) => {
        item.order = idx + 1;
      });
    }
  }
};
</script>

<style scoped>
.menu-editor-container {
  max-width: 100%;
}

.menu-item-row {
  background-color: #f8f9fa;
}

.sort-handle {
  cursor: move;
}

.sort-handle:hover {
  color: #495057 !important;
}

.is-invalid {
  border-color: #dc3545;
}

.invalid-feedback {
  display: block;
  width: 100%;
  margin-top: 0.25rem;
  font-size: 0.875em;
  color: #dc3545;
}
</style>
